import { ReactGrid } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import { throttle } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, FormControl, Spinner } from 'react-bootstrap';
import { Clipboard, Plus } from 'react-bootstrap-icons';
import { CSVLink } from 'react-csv';
import { MultiSelect } from 'react-multi-select-component';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS, isMicasa, months, ROLE_SALES_MANAGER } from '../../helpers/constants';
import {
  copyToClipboard,
  downloadFileFromBase64,
  formatInventoryUnit,
  formatLenderAndLoneTypeInfo,
  splitFullName,
  splitPropertyInfo
} from '../../helpers/global';
import { combinedDropdownCell } from '../common/CombinedDropdownCell';
import { dealboardInventoryCellTemplate } from '../common/DealboardInventoryCell';
import { dealboardCommissionDueCellTemplate } from '../common/DealboardCommissionDueCell';
import { dropdownCell } from '../common/DealboardSelectCell';
import FloatingButton from '../common/FloatingButton';
import { checkboxActionCellTemplate } from '../common/PnLActionCell';
import { editableCellTemplate } from '../common/PnLEditableCell';
import { pnlLinkCellTemplate } from '../common/PnLLinkCell';
import UnderlineButton from '../common/UnderlineButton';
import CreateSideBar from '../crm/CreateSidebar';
import { ContactEditSideBar } from '../crm/SearchCrm';
import InventoryPicker from '../inventory/InventoryPicker';
import DochubSidebar from '../pipeline/DochubSidebar';
import { InventoryEditSideBar } from '../pipeline/Pipeline';
import AlertModal from '../AlertModal';
import { isAdmin } from '../../helpers/roleHelpers';

const getYears = () => {
  const nowYear = new Date().getFullYear();
  const years = [nowYear];
  for (let index = 1; index <= 5; index++) {
    years.push(nowYear - index);
  }
  return years.reverse();
};

const calculateCommissionDueForUnit = (contact, inventoryUnit, { includeBonuses = true } = {}) => {
  if (!inventoryUnit) return null;

  const rawInvoiceValue = inventoryUnit?.invoice;
  const inventoryInvoice = Number.isFinite(rawInvoiceValue) ? Number(rawInvoiceValue) : 0;
  const salesPrice = Number(contact?.salesPrice || 0);
  const commissionLessValue =
    inventoryUnit?.commissionSheetLessOptions?.reduce((a, b) => a + Number(b?.value || 0), 0) || 0;
  const netCommissionsTotal = salesPrice - commissionLessValue;
  const optionsTotal =
    inventoryInvoice +
    (inventoryUnit?.commissionSheetOptions?.reduce((a, b) => a + Number(b?.value || 0), 0) || 0);
  const grossProfit = netCommissionsTotal - optionsTotal;

  let commissionDue;
  if (isMicasa) {
    commissionDue = grossProfit >= 20000 ? 0.25 * grossProfit : 0.2 * grossProfit;
  } else {
    commissionDue = grossProfit / 2;
  }

  if (!includeBonuses) return commissionDue;

  const curtailmentBonus = Number(contact?.additionalFields?.curtailmentBonus || 0);
  const teamLeaderBonus = Number(contact?.additionalFields?.teamLeaderBonus || 0);
  return commissionDue + curtailmentBonus + teamLeaderBonus;
};

const getCommissionDueValuesForContact = contact => {
  const units = contact?.inventoryUnit || [];
  if (!units.length) return [];

  const includeBonuses = units.length === 1;
  return units.map(unit => calculateCommissionDueForUnit(contact, unit, { includeBonuses }));
};

const formatCommissionDueValues = (values, separator = '\n') => {
  if (!values?.length) return '-';
  return values.map(value => (typeof value === 'number' ? value.toFixed(2) : '-')).join(separator);
};

const getCommissionDueDisplayValue = contact => formatCommissionDueValues(getCommissionDueValuesForContact(contact), ' | ');

const getInventoryUnitsDisplayValue = (contact, separator = ' | ') => {
  const units = contact?.inventoryUnit || [];
  if (!units.length) return '-';
  return units.map(unit => formatInventoryUnit(unit)).join(separator);
};

const dealboardHeaders = [
  {
    label: 'Buyer',
    key: 'buyer',
    valueGetter: c => splitFullName(c?.buyer)
  },
  {
    label: 'Date of Deposit',
    key: 'dateOfDeposit',
    valueGetter: c => c?.dateOfDeposit && moment(c?.dateOfDeposit).format('MMMM Do YYYY')
  },
  { label: 'Salesperson', key: 'salesperson' },
  {
    label: 'Status',
    key: 'status',
    valueGetter: c => c?.status || '-',
    width: 250
  },
  {
    label: 'Lender / Loan Type',
    key: 'lenderLoanType',
    valueGetter: c => formatLenderAndLoneTypeInfo(c) || '-',
    isEditable: true,
    width: 300
  },
  {
    label: 'Manufacturer Name / Model / Size Overall',
    key: 'inventoryUnit',
    valueGetter: getInventoryUnitsDisplayValue,
    width: 600
  },
  {
    label: 'Property Address, Property City',
    key: 'property',
    valueGetter: c => splitPropertyInfo(c?.propertyAddress, c?.propertyCity),
    width: 350
  },
  {
    label: 'Commission Due',
    key: 'Commission Due',
    valueGetter: getCommissionDueDisplayValue,
    width: 200
  },
  { label: 'Down Payment', key: 'downPayment' },
  { label: 'Notes', key: 'dealboardNotes', width: 500, isEditable: true }
];

const defaultWidths = {
  action: 120,
  ...Object.fromEntries(dealboardHeaders.map(header => [header.label, header.width || 200]))
};

const SelectionBox = ({
  excludedContracts,
  selectedMonth,
  onMonthChange,
  selectedYear,
  onYearChange,
  years,
  salesLocations,
  selectedSalesLocation,
  onSalesLocationChange,
  headers,
  filteredContacts,
  printDocument,
  printing,
  searchQuery,
  setSearchQuery,
  salesManagers,
  onSalesManagerChange,
  selectedSalesManager,
  statusOptions,
  selectedStatus,
  onStatusChange
}) => {
  const dataForCsv = useMemo(() => {
    return filteredContacts
      .filter(c => !excludedContracts.includes(c['_id']))
      .map(c => {
        const rowObj = {};
        headers.forEach(({ key, valueGetter }) => {
          rowObj[key] = valueGetter ? valueGetter(c) : c[key];
        });
        return rowObj;
      });
  }, [headers, filteredContacts, excludedContracts]);

  return (
    <Card className="border rounded">
      <Card.Header className="bg-primary">
        <h5 className="mb-0 text-white midFont">Dealboard</h5>
      </Card.Header>
      <Card.Body className="p-1">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-1">
          {[
            {
              title: 'Search',
              type: 'text',
              value: searchQuery,
              onChange: setSearchQuery,
              hint: 'Search by buyer name...'
            },
            {
              title: 'Sales Locations',
              options: salesLocations.map(s => s.location),
              optionValues: salesLocations.map(s => s._id),
              value: selectedSalesLocation,
              onChange: onSalesLocationChange,
              hide: !isAdmin()
            },
            {
              title: 'Sales Managers',
              options: ['All', ...salesManagers.map(s => s.name)],
              optionValues: [null, ...salesManagers.map(s => s._id)],
              value: selectedSalesManager,
              onChange: onSalesManagerChange,
              hide: !isAdmin()
            },
            { title: 'Select Month', options: ['', ...months], value: selectedMonth, onChange: onMonthChange },
            { title: 'Select Year', options: ['', ...years], value: selectedYear, onChange: onYearChange },
            {
              title: 'Status',
              options: ['All', ...statusOptions.map(s => s.label)],
              optionValues: [null, ...statusOptions.map(s => s.value)],
              value: selectedStatus,
              onChange: onStatusChange
            }
          ].map(({ title, options, optionValues, value, onChange, multiple, type, hint, hide, loading }) => (
            <div key={title} className="px-4 mt-3 flex-grow-1" style={{ display: hide ? 'none' : 'block' }}>
              <h6 className="mb-1 smallFont">{title}</h6>
              {multiple ? (
                <MultiSelect
                  showCheckbox
                  closeOnChangedValue={false}
                  className="midFont"
                  value={value.map(v => ({ label: v, value: v }))}
                  options={options.map(o => ({ label: o, value: o === 'Unassigned' ? null : o }))}
                  onChange={list => onChange(list.map(lo => lo.value))}
                />
              ) : type === 'text' ? (
                <FormControl
                  value={value}
                  size="sm"
                  onChange={e => onChange(e.target.value)}
                  required
                  placeholder={hint}
                />
              ) : (
                <div style={{ position: 'relative' }}>
                  <FormControl
                    as="select"
                    value={value}
                    size="sm"
                    onChange={e => onChange(e)}
                    required
                    disabled={loading}
                  >
                    {options.map((option, idx) => (
                      <option key={option} value={optionValues ? optionValues[idx] : option}>
                        {option}
                      </option>
                    ))}
                  </FormControl>
                </div>
              )}
            </div>
          ))}
          <Col xs={12} className="mt-3 mb-1 text-right">
            <Button
              size="md"
              className="mx-2"
              onClick={printDocument}
              disabled={printing || filteredContacts.length === 0}
            >
              Print
            </Button>
            <CSVLink data={dataForCsv} headers={headers} filename="data.csv" className="btn btn-primary">
              Export as CSV
            </CSVLink>
          </Col>
        </div>
      </Card.Body>
    </Card>
  );
};

const Dealboard = ({ appChoices }) => {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [years] = useState(getYears());
  const [selectedYear, setSelectedYear] = useState(years[years.length - 1]);
  const [selectedMonth, setSelectedMonth] = useState(months[new Date().getMonth()]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [salesLocations] = useState(appChoices.find(ac => ac.key === 'salesLocation').values);
  const [salesManagers, setSalesManagers] = useState([]);
  const [selectedSalesLocation, setSelectedSalesLocation] = useState(salesLocations[0]._id);
  const [selectedSalesManager, setSelectedSalesManager] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [excludedContracts, setExcludedContracts] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [editingInventory, setEditingInventory] = useState(null);
  const [openDochubMeta, setOpenDochubMeta] = useState(null);
  const [createCrmMeta, setCreateCrmMeta] = useState(null);
  const [pendingFields, setPendingFields] = useState({});
  const [updateContactMetadata, setUpdateContactMetadata] = useState(null);
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('dealboard-column-widths');
    return saved ? JSON.parse(saved) : defaultWidths;
  });
  const [filteredSalesManagers, setFilteredSalesManagers] = useState([]);
  const [inventoryPopupMeta, setInventoryPopupMeta] = useState(null);
  const [removingInventory, setRemovingInventory] = useState(false);
  const [removeUnitAlertMeta, setRemoveUnitAlertMeta] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const onUnitSelect = unit => {
    setInventoryPopupMeta(prev => ({ ...prev, selectedUnit: unit }));
  };

  const onInventorySubmit = inventoryList => {
    const newUnit = inventoryList[0];
    const { contactId } = inventoryPopupMeta || {};

    if (!contactId) {
      setInventoryPopupMeta(null);
      return;
    }

    const contactObj = contacts.find(c => c._id === contactId);
    if (contactObj) {
      const pendingKey = `${contactId}-inventoryUnit`;
      updateContact(contactObj, 'inventoryUnit', [...inventoryPopupMeta?.contactUnits, newUnit?._id], pendingKey);
    }

    setInventoryPopupMeta(null);
  };

  const handleColumnResized = (columnId, newWidth) => {
    const clamped = Math.max(80, Math.min(600, newWidth));
    setColumnWidths(prev => {
      const updated = { ...prev, [columnId]: clamped };
      saveColumnWidths(updated);
      return updated;
    });
  };

  useEffect(() => {
    if (searchQuery) {
      setSelectedYear('');
      setSelectedMonth('');
    } else {
      setSelectedYear(years[years.length - 1]);
      setSelectedMonth(months[new Date().getMonth()]);
    }
  }, [searchQuery, years]);

  const onExcludeContactChange = (id, checked) => {
    if (checked) {
      const idx = excludedContracts.indexOf(id);
      if (idx !== -1) excludedContracts.splice(idx, 1);
    } else {
      if (!excludedContracts.includes(id)) excludedContracts.push(id);
    }
    setExcludedContracts([...excludedContracts]);
  };

  const printDocument = async () => {
    toast.info('Submitting report, please wait...');
    setPrinting(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.DOCHUB_PRINT_TABLE,
      requestBody: {
        table: [
          dealboardHeaders.map(h => h.label),
          ...filteredContacts
            .filter(c => !excludedContracts.includes(c['_id']))
            .map(c =>
              dealboardHeaders.map(({ key, valueGetter }) => {
                if (valueGetter) return valueGetter(c);
                return c[key] ? c[key] : '-';
              })
            )
        ],
        fileName: `dealboard-${new Date().toDateString()}.pdf`
      }
    });

    setPrinting(false);
    if (error) return toast.error(error);

    toast.success('Document created successfully, file is being downloaded...');
    const { fileName, data, mimeType } = response;
    downloadFileFromBase64({ fileName, data, mimeType });
  };

  const onContactDelete = contactId => {
    const index = filteredContacts.findIndex(c => c['_id'] === contactId);
    if (index !== -1) {
      filteredContacts.splice(index, 1);
      setFilteredContacts([...filteredContacts]);
    }
    setUpdateContactMetadata(null);
  };

  const onCRMFileLoaded = (crmId, fileNames) => {
    const index = filteredContacts.findIndex(c => c['_id'] === crmId);
    if (index !== -1) {
      filteredContacts[index]['fileNames'] = fileNames;
      setFilteredContacts([...filteredContacts]);
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_SEARCH,
      requestBody: {
        filter: {
          inventoryUnit: { $ne: null },
          exportedContract: null,
          salesLocation: selectedSalesLocation
        },
        sort: '-dateOfDeposit',
        salesManager: isAdmin()
          ? selectedSalesManager === 'All'
            ? null
            : selectedSalesManager
          : null
      }
    });

    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }
    setContacts(response);
    setLoading(false);
  };

  const fetchUsers = async () => {
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_LIST,
      requestBody: {
        filter: {
          role: ROLE_SALES_MANAGER
        }
      }
    });
    if (error) {
      toast.error(error);
      return;
    }
    setSalesManagers(response);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (isAdmin()) {
          await fetchUsers();
        }
        await fetchContacts();
      } catch (error) {
        toast.error(error);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [selectedSalesManager, selectedSalesLocation]);

  useEffect(() => {
    setLists(contacts.sort((a, b) => new Date(b.dateOfDeposit) - new Date(a.dateOfDeposit)));
  }, [contacts]);

  const filterContacts = () => {
    const temp = lists.filter(contact => {
      if (contact?.salesLocation?._id !== selectedSalesLocation) return false;

      if (selectedStatus && selectedStatus !== 'All' && contact?.status !== selectedStatus) return false;

      if (searchQuery && !contact?.buyer?.toLowerCase().includes(searchQuery.toLowerCase())) return false;

      const dt = contact?.dateOfDeposit ? new Date(contact?.dateOfDeposit) : null;
      if (selectedMonth) {
        const m = months.indexOf(selectedMonth);
        if (m !== dt?.getMonth()) return false;
      }
      if (selectedYear) {
        if (selectedYear !== dt?.getFullYear()) return false;
      }
      return true;
    });
    setFilteredContacts([...temp]);
  };

  const onUpdateInventory = updatedInventory => {
    const contactToBeUpdatedIndex = filteredContacts?.findIndex(
      filteredContact => filteredContact?._id === editingInventory?.contactId
    );

    if (contactToBeUpdatedIndex !== -1) {
      filteredContacts[contactToBeUpdatedIndex].inventoryUnit = [
        ...filteredContacts[contactToBeUpdatedIndex].inventoryUnit?.map(unit =>
          unit?._id === updatedInventory?._id ? updatedInventory : unit
        )
      ];
      setFilteredContacts([...filteredContacts]);
    }

    setEditingInventory(null);
  };

  const onDeleteInventory = (deletedInventoryId, contactId) => {
    const contactToBeUpdatedIndex = filteredContacts?.findIndex(
      filteredContact => filteredContact?._id === contactId || editingInventory?.contactId
    );

    if (contactToBeUpdatedIndex !== -1) {
      filteredContacts[contactToBeUpdatedIndex].inventoryUnit = [
        ...filteredContacts[contactToBeUpdatedIndex].inventoryUnit?.filter(unit => unit?._id !== deletedInventoryId)
      ];
      setFilteredContacts([...filteredContacts]);
    }

    setEditingInventory(null);
  };

  const updateBuyerName = response => {
    setContacts(contacts?.map(contact => (contact?._id === response?._id ? response : contact)));

    setUpdateContactMetadata(null);
  };

  const updateContact = useCallback(async (contact, field, value, key) => {
    setPendingFields(prev => ({ ...prev, [key]: true }));

    const updateBody = { _id: contact['_id'], [field]: value };
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_WITH_ID(contact['_id']),
      requestBody: updateBody,
      method: 'PUT'
    });
    if (error) {
      setPendingFields(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return toast.error(error);
    }

    setContacts(prev => prev.map(c => (c['_id'] === contact['_id'] ? response : c)));

    toast.success('Updated Successfully');

    setPendingFields(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setUpdateContactMetadata(null);
  }, []);

  const handleCellsChanged = changes => {
    if (changes.length !== 1) return;
    changes.forEach(change => {
      const { rowId, columnId, newCell } = change;
      if (!rowId.startsWith('data-')) return;

      const index = parseInt(rowId.split('-')[1]);
      const contact = filteredContacts[index];
      if (!contact) return;

      const header = dealboardHeaders.find(h => h.label === columnId);
      if (!header) return;
      if (newCell.nonEditable || !header.isEditable) return;

      const value = newCell.text;
      if (header.isEditable) {
        const key = `${contact._id}-${header.key}`;
        updateContact(contact, header.key, value, key);
      }
    });
  };

  useEffect(() => {
    if (loading) return;
    filterContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists, selectedYear, selectedMonth, selectedSalesLocation, searchQuery, loading, selectedStatus]);

  const onYearChange = e => {
    setSelectedYear(e.target.value ? Number(e.target.value) : null);
  };
  const onMonthChange = e => {
    setSelectedMonth(e.target.value);
  };
  const onSalesLocationChange = e => {
    setSelectedSalesLocation(e.target.value);
    setSelectedSalesManager(null);
  };
  const onSalesManagerChange = e => {
    setSelectedSalesManager(e.target.value);
  };

  const updateList = co => {
    setContacts(prev => prev.map(c => (c._id === co._id ? co : c)));
  };

  const handleCopyRow = contact => {
    const headersToCopy = dealboardHeaders.filter(h => h.label !== 'Notes');
    const values = headersToCopy.map(header => {
      let value;
      if (header.valueGetter) {
        value = header.valueGetter(contact);
      } else {
        value = contact[header.key];
      }
      if (typeof value === 'number') {
        value = value.toFixed(2);
      }
      return value || '-';
    });
    copyToClipboard(values.join('\t'));
    toast.success('Row copied to clipboard');
  };

  const columns = useMemo(
    () => [
      {
        columnId: 'action',
        width: columnWidths['action'],
        resizable: true,
        headerCell: () => 'Action'
      },
      ...dealboardHeaders.map(header => ({
        columnId: header.label,
        width: columnWidths[header.label],
        resizable: true,
        headerCell: () => header.label
      }))
    ],
    [columnWidths]
  );

  const lenderOptions = useMemo(() => {
    return (appChoices.find(ac => ac.key === 'lender')?.values || []).map(v => ({
      label: v.lienholderCorporateName,
      value: v._id
    }));
  }, [appChoices]);

  const loanTypeOptions = useMemo(() => {
    return (appChoices.find(ac => ac.key === 'Loan Type')?.values || []).map(v => ({
      label: v,
      value: v
    }));
  }, [appChoices]);

  const statusOptions = useMemo(() => {
    return (appChoices.find(ac => ac.key === 'Contact Status')?.values || []).map(v => ({
      label: v,
      value: v
    }));
  }, [appChoices]);

  const getRows = () => {
    const headerRow = {
      rowId: 'header',
      height: 60,
      cells: [
        {
          type: 'header',
          text: 'Action',
          className: 'bg-dark text-light text-center d-flex justify-content-center',
          style: { fontSize: '12px', fontWeight: 'bold' }
        },
        ...dealboardHeaders.map(h => ({
          type: 'header',
          text: h.label,
          className: 'bg-dark text-light d-flex justify-content-center',
          style: { fontSize: '12px', fontWeight: 'bold' }
        }))
      ]
    };

    const commonCellStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: 'semibold'
    };

    const dataRows = filteredContacts.map((contact, idx) => {
      const cells = [];

      cells.push({
        type: 'checkboxAction',
        checked: !excludedContracts.includes(contact._id),
        onCheckboxChange: checked => onExcludeContactChange(contact._id, checked),
        onCopyClick: () => handleCopyRow(contact),
        style: commonCellStyle
      });

      dealboardHeaders.forEach(header => {
        const { key, isEditable } = header;

        if (key === 'lenderLoanType') {
          const currentLenderId = contact?.lender?._id || null;
          const currentLoanType = contact?.loanType || null;

          const pendingLenderKey = `${contact._id}-lender`;
          const pendingLoanTypeKey = `${contact._id}-loanType`;

          const isLenderLoading = Boolean(pendingFields[pendingLenderKey]);
          const isLoanTypeLoading = Boolean(pendingFields[pendingLoanTypeKey]);

          cells.push({
            type: 'combinedDropdown',
            lenderValue: currentLenderId,
            loanTypeValue: currentLoanType,
            lenderOptions,
            loanTypeOptions,
            isLenderLoading,
            isLoanTypeLoading,
            nonEditable: false,
            text: formatLenderAndLoneTypeInfo(contact) || '-',
            onLenderSelect: newLenderId => {
              const key2 = `${contact._id}-lender`;
              updateContact(contact, 'lender', newLenderId, key2);
            },
            onLoanTypeSelect: newLoanType => {
              const key2 = `${contact._id}-loanType`;
              updateContact(contact, 'loanType', newLoanType, key2);
            },
            style: {
              ...commonCellStyle,
              opacity: isLenderLoading || isLoanTypeLoading ? 0.5 : 1,
              backgroundColor: isLenderLoading || isLoanTypeLoading ? '#fff3cd' : 'inherit'
            }
          });

          return;
        }

        if (key === 'buyer') {
          const rawValue = header.valueGetter(contact);
          const displayValue = rawValue || '-';
          cells.push({
            type: 'editable',
            text: displayValue,
            nonEditable: true,
            onClick: () => {
              setUpdateContactMetadata(contact);
            },
            style: {
              ...commonCellStyle,
              backgroundColor: '#F8F9FA'
            }
          });
          return;
        }

        if (key === 'Commission Due') {
          const commissionValues = getCommissionDueValuesForContact(contact);
          cells.push({
            type: 'commissionDue',
            commissionValues,
            text: formatCommissionDueValues(commissionValues, ' | '),
            onClick: () => {
              setOpenDochubMeta(contact?._id);
            },
            style: {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0.5rem',
              fontSize: '0.875rem',
              lineHeight: 1.2,
              whiteSpace: 'normal',
              overflowY: 'auto',
              backgroundColor: '#F8F9FA'
            }
          });
          return;
        }

        if (key === 'inventoryUnit') {
          const isPending = Boolean(pendingFields[`${contact._id}-inventoryUnit`]);

          cells.push({
            type: 'inventoryUnit',
            units: contact?.inventoryUnit,
            nonEditable: true,
            isLoading: isPending,
            text: getInventoryUnitsDisplayValue(contact),
            addButtonText: !contact.inventoryUnit?.length && 'Add Inventory Unit',
            inventoryUnits: contact.inventoryUnit,
            onLinkNew: () =>
              setInventoryPopupMeta(prev => ({
                ...prev,
                contactId: contact._id,
                contactUnits: contact.inventoryUnit?.map((_, i) => _?._id),
                allUnitIds: contact.inventoryUnit?.map((_, i) => _?._id)
              })),
            onChangeClick: unitId => {
              setInventoryPopupMeta(prev => ({
                ...prev,
                contactId: contact._id,
                contactUnits: contact.inventoryUnit?.filter(unit => unit?._id !== unitId)?.map(_ => _?._id),
                allUnitIds: contact.inventoryUnit?.map((_, i) => _?._id)
              }));
            },
            onEditClick: unit => setEditingInventory({ unit, contactId: contact?._id }),
            onRemoveClick: id => onRemoveUnitClick(contact, id),
            style: {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              padding: '0.5rem',
              fontSize: '0.875rem',
              lineHeight: 1.2,
              whiteSpace: 'normal',
              overflowY: 'auto',
              opacity: isPending ? 0.5 : 1,
              backgroundColor: isPending ? '#fff3cd' : 'inherit'
            }
          });
          return;
        }

        if (key === 'status') {
          const rawValue = header.valueGetter ? header.valueGetter(contact) : contact[header.key];
          const displayValue = rawValue || '-';
          const isPending = Boolean(pendingFields[`${contact._id}-status`]);

          cells.push({
            type: 'dropdown',
            text: displayValue,
            isEditable: true,
            options: statusOptions,
            onSelect: value => {
              const key2 = `${contact._id}-status`;
              updateContact(contact, 'status', value, key2);
            },
            isLoading: isPending,
            style: {
              ...commonCellStyle,
              opacity: isPending ? 0.5 : 1,
              backgroundColor: isPending ? '#fff3cd' : 'inherit'
            }
          });
          return;
        }

        const rawValue = header.valueGetter ? header.valueGetter(contact) : contact[header.key];
        const displayValue = typeof rawValue === 'number' ? rawValue.toFixed(2) : rawValue || '-';

        cells.push({
          type: 'text',
          text: displayValue,
          nonEditable: !isEditable,
          className: !isEditable ? 'bg-light' : '',
          style: {
            ...commonCellStyle,
            opacity: Boolean(pendingFields[`${contact._id}-${header.key}`]) ? 0.5 : 1,
            backgroundColor: Boolean(pendingFields[`${contact._id}-${header.key}`]) ? '#fff3cd' : 'inherit'
          }
        });
      });

      const unitCount = contact?.inventoryUnit?.length || 0;
      const cappedCount = Math.min(unitCount, 5);
      const rowHeight = cappedCount < 2 ? 60 : cappedCount * 35;

      return {
        rowId: `data-${idx}`,
        height: rowHeight,
        cells
      };
    });

    return [headerRow, ...dataRows];
  };

  const rows = getRows();

  useEffect(() => {
    const handleCopy = e => {
      if (e.ctrlKey && e.key === 'c') {
        e.preventDefault();
        const text = selectedAreas
          .flatMap(area => {
            const rowsArr = [];
            for (let r = area.top; r <= area.bottom; r++) {
              const rowCells = [];
              for (let c = area.left; c <= area.right; c++) {
                const cell = rows[r]?.cells[c];
                rowCells.push(cell?.text || '');
              }
              rowsArr.push(rowCells.join('\t'));
            }
            return rowsArr;
          })
          .join('\n');
        navigator.clipboard.writeText(text);
      }
    };

    document.addEventListener('keydown', handleCopy);
    return () => document.removeEventListener('keydown', handleCopy);
  }, [selectedAreas, rows]);

  useEffect(() => {
    if (selectedSalesLocation) {
      const filtered = salesManagers.filter(m => m.salesLocation?._id === selectedSalesLocation);
      setFilteredSalesManagers(filtered);
    } else {
      setFilteredSalesManagers(salesManagers);
    }
  }, [selectedSalesLocation, salesManagers]);

  const saveColumnWidths = useCallback(
    throttle(widths => {
      localStorage.setItem('dealboard-column-widths', JSON.stringify(widths));
    }, 1000),
    []
  );

  const handleCopyAll = useCallback(() => {
    if (filteredContacts.length === 0) {
      toast.error('No rows to copy');
      return;
    }
    const headersToCopy = dealboardHeaders.filter(h => h.label !== 'Notes');
    const data = filteredContacts
      .map(contact =>
        headersToCopy
          .map(header => {
            let value;
            if (header.valueGetter) {
              value = header.valueGetter(contact);
            } else {
              value = contact[header.key];
            }
            return typeof value === 'number' ? value.toFixed(2) : value || '-';
          })
          .join('\t')
      )
      .join('\n');
    copyToClipboard(data);
    toast.success('All rows copied to clipboard');
  }, [filteredContacts]);

  const onRemoveUnitClick = (contact, inventoryId) => {
    setRemoveUnitAlertMeta({ inventoryId, contact });
  };

  const removeInventoryUnit = async () => {
    setRemovingInventory(true);

    const { inventoryId, contact } = removeUnitAlertMeta;

    let newUnitIds = contact.inventoryUnit.map(u => u._id);
    const removingIndex = newUnitIds.indexOf(inventoryId);
    newUnitIds.splice(removingIndex, 1);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_WITH_ID(contact._id),
      requestBody: { inventoryUnit: newUnitIds },
      method: 'PUT'
    });

    setRemovingInventory(false);

    if (error) {
      toast(error, {
        type: 'error'
      });
      return;
    }

    toast(`Inventory unit removed successfully!`, {
      type: 'success'
    });

    updateList(response);
    setRemoveUnitAlertMeta(null);
  };

  useEffect(() => {
    const saved = localStorage.getItem('pnl-column-widths');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setColumnWidths(prev => ({ ...defaultWidths, ...parsed }));
      } catch (e) {
        console.error('Error parsing saved column widths:', e);
      }
    }
  }, []);

  return (
    <div className="px-1 py-4">
      <SelectionBox
        years={years}
        selectedMonth={selectedMonth}
        onMonthChange={onMonthChange}
        selectedYear={selectedYear}
        onYearChange={onYearChange}
        salesLocations={salesLocations}
        selectedSalesLocation={selectedSalesLocation}
        onSalesLocationChange={onSalesLocationChange}
        printing={printing}
        filteredContacts={filteredContacts}
        printDocument={printDocument}
        headers={dealboardHeaders}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        excludedContracts={excludedContracts}
        salesManagers={filteredSalesManagers}
        onSalesManagerChange={onSalesManagerChange}
        selectedSalesManager={selectedSalesManager}
        selectedStatus={selectedStatus}
        onStatusChange={e => setSelectedStatus(e.target.value)}
        statusOptions={statusOptions || []}
      />
      <hr />
      <div className="d-flex align-items-center gap-2 mb-2">
        <h6 className="mb-0">Dealboard:</h6>
        {filteredContacts?.length > 0 && (
          <UnderlineButton
            onClick={handleCopyAll}
            variant="success"
            text="Copy all rows"
            disabled={filteredContacts.length === 0}
            Icon={Clipboard}
            iconSize={14}
          />
        )}
      </div>
      <div
        className="overflow-auto hide-scrollbar"
        style={{
          maxHeight: filteredContacts?.length ? 'calc(100vh - 100px)' : 'auto'
        }}
      >
        <ReactGrid
          rows={rows}
          columns={columns}
          onCellsChanged={handleCellsChanged}
          onColumnResized={handleColumnResized}
          enableRangeSelection
          enableColumnSelection
          stickyTopRows={1}
          stickyLeftColumns={2}
          horizontalStickyBreakpoint={100}
          selectedAreas={selectedAreas}
          onSelectionChanged={setSelectedAreas}
          customCellTemplates={{
            checkboxAction: checkboxActionCellTemplate,
            link: pnlLinkCellTemplate,
            editable: editableCellTemplate,
            dropdown: dropdownCell,
            combinedDropdown: combinedDropdownCell,
            inventoryUnit: dealboardInventoryCellTemplate,
            commissionDue: dealboardCommissionDueCellTemplate
          }}
          style={{ minWidth: `${Object.values(columnWidths).reduce((a, b) => a + b, 0)}px` }}
        />
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'none',
              zIndex: 1000
            }}
          >
            <Spinner animation="border" />
          </div>
        )}
      </div>
      {!filteredContacts?.length && <h6 className="my-2 text-center">No contacts to show</h6>}
      <h6 className="text-right text-muted mt-2">
        Total Reports: <span className="text-dark">{filteredContacts.length}</span>
      </h6>

      <CreateSideBar
        onContactAdd={contact => {
          setContacts(prev => [...prev, contact]);
          setCreateCrmMeta(null);
        }}
        appChoices={appChoices}
        show={createCrmMeta}
        onHide={() => setCreateCrmMeta(null)}
        prefillContact={createCrmMeta?.prefillContact}
      />

      <FloatingButton text="Add New Contact" variant="success" Icon={Plus} onClick={() => setCreateCrmMeta({})} />

      <DochubSidebar
        show={openDochubMeta}
        onHide={() => setOpenDochubMeta(null)}
        appChoices={appChoices}
        source={'Contact'}
        contactId={openDochubMeta}
        onUpdateDocHub={(updatedResponse, isPrintOperation = false) => {
          setContacts(prev => prev.map(c => (c._id === updatedResponse._id ? updatedResponse : c)));
          if (!isPrintOperation) {
            setOpenDochubMeta(null);
          }
        }}
      />

      <InventoryEditSideBar
        appChoices={appChoices}
        onHide={() => setEditingInventory(null)}
        editingInventory={editingInventory?.unit}
        onInventoryEdit={updatedInventory => {
          onUpdateInventory(updatedInventory);
        }}
        onInventoryDelete={deletedInventoryId => {
          onDeleteInventory(deletedInventoryId);
        }}
      />

      <ContactEditSideBar
        editingContact={updateContactMetadata}
        appChoices={appChoices}
        onContactEdit={updateBuyerName}
        onContactDelete={onContactDelete}
        onCRMFileLoaded={onCRMFileLoaded}
        onContactEditSideBarHide={() => setUpdateContactMetadata(null)}
      />

      <InventoryPicker
        show={inventoryPopupMeta !== null}
        onSubmit={onInventorySubmit}
        selectedUnits={inventoryPopupMeta?.selectedUnit ? [inventoryPopupMeta.selectedUnit] : []}
        onUnitSelect={onUnitSelect}
        onInventoryPickerClose={() => setInventoryPopupMeta(null)}
        excludedUnitIds={inventoryPopupMeta?.allUnitIds}
      />
      <AlertModal
        show={removeUnitAlertMeta != null}
        alertText="Are you sure to remove this inventory unit from contact?"
        showProgress={removingInventory}
        progressText="Removing unit..."
        onDismissClick={() => setRemoveUnitAlertMeta(null)}
        onHide={() => setRemoveUnitAlertMeta(null)}
        onContinueClick={removeInventoryUnit}
      />
    </div>
  );
};

export default Dealboard;
